import { Bot, Pickaxe, UserRound, Volume2, VolumeX, Wifi, WifiOff, X } from "lucide-react";
import { clearedCombatState } from "./combatState";
import { AdminConsole } from "./screens/AdminConsole";
import { AuthDialog } from "./screens/AuthDialog";
import { AuthEntryScreen } from "./screens/AuthEntryScreen";
import { FatalScreen, LoadingScreen, OfflineScreen } from "./screens/ConnectionScreens";
import { GameScreen } from "./screens/GameScreen";
import { HubScreen } from "./screens/HubScreen";
import { MultiplayerRoomScreen } from "./screens/MultiplayerRoomScreen";
import { RaceScreen } from "./screens/RaceScreen";
import { RaidSettlement, Settlement } from "./screens/Settlement";
import { QUALITY_LABELS } from "./ui/EquipmentIcon";
import { formatOfflineDuration } from "./ui/format";
import { ResourceIcon } from "./ui/Resources";
import type { useAppController } from "./useAppController";

export function AppView(props: ReturnType<typeof useAppController>) {
  const {
    stage,
    error,
    simStatus,
    connected,
    muted,
    toggleMuted,
    hubTab,
    setHubTab,
    connectionState,
    browserOnline,
    retryOffline,
    offlineRewardsOpen,
    offlineRewards,
    dismissOfflineRewards,
    setError,
    loading,
    enterSession,
    api,
    me,
    inventory,
    growth,
    lifeProfile,
    refreshPlayerState,
    social,
    tasks,
    busy,
    startRun,
    purchaseDiamondHeal,
    changeOffline,
    enterRoom,
    enterRace,
    changeEquipment,
    setLifeEnhancementItemId,
    changeGrowth,
    convertWorkshopResources,
    changeLife,
    refreshLifeProjection,
    lifeEnhancementItemId,
    redeemCode,
    claimTask,
    claimAllTasks,
    changeSocial,
    theme,
    fontScale,
    effectSettings,
    colorBlind,
    setTheme,
    toggleColorBlind,
    changeFontScale,
    changeEffectChannel,
    changeAutoSellQuality,
    setAuthMode,
    session,
    setAdminOpen,
    view,
    runEnding,
    runQueued,
    act,
    audioRef,
    roomView,
    updateRoomView,
    leaveMultiplayer,
    raceView,
    updateRaceView,
    applyCombatState,
    loadPlayer,
    returnToHub,
    acknowledgeRoomSettlement,
    adminOpen,
    authMode,
    setMe,
    setInventory,
    setGrowth,
    setLifeProfile,
    setTasks,
    setSocial,
    logout,
  } = props;
  if (stage === "booting") return <LoadingScreen error={error} />;

  return (
    <div
      className={`app-shell${stage === "hub" ? " has-hub-navigation" : ""}${stage === "game" ? " is-game-stage" : ""}`}
      data-sim-status={simStatus.status}
    >
      <header className="app-header">
        <div className="brand-lockup">
          <Pickaxe size={22} strokeWidth={2.3} />
          <div>
            <strong>Breacher</strong>
          </div>
        </div>
        <div className="header-actions">
          {stage !== "auth" && (
            <span
              className={`connection-indicator ${connected ? "is-online" : ""}`}
              title={connected ? "已连接" : "重连中"}
              aria-label={connected ? "已连接" : "重连中"}
            >
              {connected ? <Wifi size={17} /> : <WifiOff size={17} />}
            </span>
          )}
          <button
            className={`icon-button ${muted ? "is-active" : ""}`}
            onClick={toggleMuted}
            title={muted ? "开启声音" : "静音"}
            aria-label={muted ? "开启声音" : "静音"}
          >
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          {stage === "hub" && (
            <button
              type="button"
              className={`icon-button ${hubTab === "profile" ? "is-active" : ""}`}
              onClick={() => setHubTab("profile")}
              title="我的"
              aria-label="我的"
              aria-current={hubTab === "profile" ? "page" : undefined}
            >
              <UserRound size={18} />
            </button>
          )}
        </div>
      </header>

      {stage === "offline" ? (
        <OfflineScreen
          retrying={connectionState === "retrying"}
          browserOnline={browserOnline}
          error={error}
          onRetry={() => void retryOffline()}
        />
      ) : (
        <>
          {offlineRewardsOpen && offlineRewards && (
            <aside className="offline-reward-notice" aria-label="离线战斗收益" role="status">
              <header>
                <Bot size={18} />
                <strong>离线战斗收益</strong>
                <button
                  className="icon-button"
                  onClick={dismissOfflineRewards}
                  aria-label="关闭离线收益"
                  title="关闭离线收益"
                >
                  <X size={17} />
                </button>
              </header>
              <p>
                {formatOfflineDuration(offlineRewards.seconds)} · 推进 {offlineRewards.depths} 层
              </p>
              <div className="offline-reward-totals">
                <span>
                  经验 <b>+{offlineRewards.experience}</b>
                </span>
                <span>
                  装备 <b>{offlineRewards.equipment.length} 件</b>
                </span>
              </div>
              <div className="offline-reward-resources">
                {(["wood", "ore", "gold", "crystal"] as const).map((kind) => (
                  <span key={kind}>
                    <ResourceIcon kind={kind} />
                    {offlineRewards[kind]}
                  </span>
                ))}
              </div>
              <small>
                {offlineRewards.settled
                  ? "本局已结算，装备按保留设置与背包容量入库。"
                  : "收益已保留在本局，撤退或死亡后结算。"}
              </small>
              {offlineRewards.equipment.length > 0 && (
                <details>
                  <summary>获得的装备</summary>
                  <ul>
                    {offlineRewards.equipment.map((item) => (
                      <li key={item.id} className={`quality-${item.quality}`}>
                        <span>{item.name}</span>
                        <small>{QUALITY_LABELS[item.quality]}</small>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </aside>
          )}
          {error && (
            <div className="error-banner" role="alert">
              <span>{error}</span>
              <button className="icon-button" onClick={() => setError("")} aria-label="关闭错误">
                <X size={17} />
              </button>
            </div>
          )}

          {!connected && !loading && stage !== "auth" && (
            <div className="connection-banner" role="status">
              <WifiOff size={15} />
              连接已暂停，正在冻结本局状态…
            </div>
          )}

          {stage === "auth" && (
            <AuthEntryScreen
              onLogin={(username, password) => enterSession(() => api.login(username, password))}
              onRegister={(username, password, passwordConfirm, nickname) =>
                enterSession(() => api.register(username, password, passwordConfirm, nickname))
              }
            />
          )}
          {stage === "fatal" && <FatalScreen error={error || "无法初始化游戏状态"} />}
          {stage === "hub" && me && inventory && growth && lifeProfile && (
            <HubScreen
              onShopChanged={refreshPlayerState}
              api={api}
              tab={hubTab}
              onTab={setHubTab}
              me={me}
              inventory={inventory}
              growth={growth}
              life={lifeProfile}
              clockNow={api.serverNow}
              social={social}
              tasks={tasks}
              busy={busy || !connected}
              onStart={startRun}
              onHeal={purchaseDiamondHeal}
              onClaimOffline={() => changeOffline(() => api.claimOffline())}
              onPurchaseOffline={(packageId) => changeOffline(() => api.purchaseOffline(packageId))}
              onOfflineSettings={(source, automation) =>
                changeOffline(() => api.updateOfflineSettings(source, automation))
              }
              onMatchRaid={() => enterRoom(() => api.matchRaid())}
              onSoloRaid={() =>
                enterRoom(async () => {
                  await api.createRoom("raid");
                  return api.startRoom(true);
                })
              }
              onCreateDuo={() => enterRoom(() => api.createRoom("duo"))}
              onJoinRoom={(code) => enterRoom(() => api.joinRoom(code))}
              onListRooms={(mode) => api.listRooms(mode)}
              onStartRace={(kind) => enterRace(() => api.startRace(kind))}
              onEquip={(itemId) => changeEquipment(() => api.equip(itemId))}
              onUnequip={(slot) => changeEquipment(() => api.unequip(slot))}
              onLock={(itemId, locked) => changeEquipment(() => api.setItemLocked(itemId, locked))}
              onEnhance={(itemId) => {
                setLifeEnhancementItemId(itemId);
                setHubTab("life");
              }}
              onReforge={(itemId, affixIndex) =>
                changeEquipment(() => api.reforgeItem(itemId, affixIndex))
              }
              onRefine={(itemId, affixIndex) =>
                changeEquipment(() => api.refineItem(itemId, affixIndex))
              }
              onCalibrate={(itemId) => changeEquipment(() => api.calibrateItem(itemId))}
              onSell={(itemIds) => changeEquipment(() => api.sellItems(itemIds))}
              onSaveSkills={(skills) => changeGrowth(() => api.configureSkills(skills))}
              onUnlockSkill={(skillId) => changeGrowth(() => api.unlockSkill(skillId))}
              onRespecSkills={() => changeGrowth(() => api.respecSkills())}
              onUpgradeWorkshop={(buildingId) =>
                changeGrowth(() => api.upgradeWorkshop(buildingId))
              }
              onConvertWorkshop={convertWorkshopResources}
              onStartLife={(request) => changeLife(() => api.startLife(request))}
              onClaimLife={() => changeLife(() => api.claimLife())}
              onStopLife={() => changeLife(() => api.stopLife())}
              onRefreshLife={refreshLifeProjection}
              lifeEnhancementItemId={lifeEnhancementItemId}
              onRedeem={redeemCode}
              onClaimTask={claimTask}
              onClaimAllTasks={claimAllTasks}
              onFriendRequest={(username) => changeSocial(() => api.friendRequest(username))}
              onFriendRespond={(userId, accept) =>
                changeSocial(() => api.friendRespond(userId, accept))
              }
              onFriendRemove={(userId) => changeSocial(() => api.friendRemove(userId))}
              onCrewCreate={(name) => changeSocial(() => api.createCrew(name))}
              onCrewJoin={(name) => changeSocial(() => api.joinCrew(name))}
              onCrewLeave={() => changeSocial(() => api.leaveCrew())}
              onCrewSupply={() => changeSocial(() => api.claimCrewSupply(), true)}
              guildActions={{
                update: (announcement, joinPolicy) =>
                  changeSocial(() => api.updateGuild(announcement, joinPolicy)),
                respondApplication: (userId, accept) =>
                  changeSocial(() => api.respondGuildApplication(userId, accept)),
                invite: (username) => changeSocial(() => api.inviteGuild(username)),
                setRole: (userId, role) => changeSocial(() => api.setGuildRole(userId, role)),
                remove: (userId) => changeSocial(() => api.removeGuildMember(userId)),
                transfer: (userId) => changeSocial(() => api.transferGuild(userId)),
                respondInvite: (guildId, accept) =>
                  changeSocial(() => api.respondGuildInvite(guildId, accept)),
                archive: () => changeSocial(() => api.archiveGuild()),
              }}
              theme={theme}
              fontScale={fontScale}
              effectSettings={effectSettings}
              muted={muted}
              colorBlind={colorBlind}
              onTheme={setTheme}
              onToggleMuted={toggleMuted}
              onToggleColorBlind={toggleColorBlind}
              onFontScale={changeFontScale}
              onEffectChannel={changeEffectChannel}
              onAutoSellQuality={changeAutoSellQuality}
              onAccount={() => setAuthMode(session?.user.guest ? "bind" : "login")}
              onAdmin={() => setAdminOpen(true)}
            />
          )}
          {stage === "game" && view?.run.status === "active" && growth && (
            <GameScreen
              view={view}
              clockNow={api.serverNow}
              growth={growth}
              busy={!connected || runEnding}
              requestQueued={runQueued}
              simReady={simStatus.status === "ready"}
              colorBlind={colorBlind}
              effectSettings={effectSettings}
              muted={muted}
              onToggleMuted={toggleMuted}
              onSlide={(axis, index, distance, automated) =>
                act(() => api.slide(axis, index, distance, view.run.boardHash, automated))
              }
              onLockRow={(row) => act(() => api.lockRow(row))}
              onSkill={(skill) => {
                audioRef.current.play("skill");
                void act(() => api.skill(skill));
              }}
              onChest={(open) => act(() => api.chest(open))}
              onRetreat={() => act(() => api.retreat(), "retreat")}
              guest={me?.user.guest ?? false}
              playerName={me?.user.nickname || "Breacher"}
              onBind={() => setAuthMode("bind")}
            />
          )}
          {stage === "room" && roomView && growth && (
            <MultiplayerRoomScreen
              view={roomView}
              clockNow={api.serverNow}
              growth={growth}
              simReady={simStatus.status === "ready"}
              colorBlind={colorBlind}
              effectSettings={effectSettings}
              busy={busy || !connected}
              onReady={(ready) => api.roomReady(ready).then(updateRoomView)}
              onStart={(solo) => api.startRoom(solo).then(updateRoomView)}
              onUpdate={(mode, visibility) => api.updateRoom(mode, visibility).then(updateRoomView)}
              onKick={(userId) => api.kickRoomMember(userId).then(updateRoomView)}
              onTransfer={(userId) => api.transferRoomOwner(userId).then(updateRoomView)}
              onLeave={leaveMultiplayer}
              onSlide={(axis, index, distance, hash, automated) =>
                api.roomSlide(axis, index, distance, hash, automated).then(updateRoomView)
              }
              onSkill={(skill, target) => {
                audioRef.current.play("skill");
                return api.roomSkill(skill, target).then(updateRoomView);
              }}
              onRetreat={(confirm) => api.roomRetreat(confirm).then(updateRoomView)}
            />
          )}
          {stage === "race" && raceView && growth && (
            <RaceScreen
              view={raceView}
              clockNow={api.serverNow}
              growth={growth}
              colorBlind={colorBlind}
              effectSettings={effectSettings}
              busy={busy || !connected}
              onSlide={(axis, index, distance, hash) =>
                api.raceSlide(axis, index, distance, hash).then(updateRaceView)
              }
              onDraft={(choice) => api.raceDraft(choice).then(updateRaceView)}
              onSkill={(skill) => {
                audioRef.current.play("skill");
                return api.raceSkill(skill).then(updateRaceView);
              }}
              onExit={() => {
                applyCombatState(clearedCombatState());
                void loadPlayer();
              }}
            />
          )}
          {stage === "settlement" && view?.run.status === "ended" && (
            <Settlement run={view.run} onContinue={returnToHub} />
          )}
          {stage === "room-settlement" && roomView && (
            <RaidSettlement
              view={roomView}
              busy={busy || !connected}
              onContinue={acknowledgeRoomSettlement}
            />
          )}

          {adminOpen && me?.user.role === "admin" && (
            <AdminConsole
              api={api}
              onClose={() => setAdminOpen(false)}
              onPlayerChanged={(userId) =>
                userId === session?.user.id ? refreshPlayerState() : undefined
              }
            />
          )}

          {authMode && session && (
            <AuthDialog
              mode={authMode}
              guest={session.user.guest}
              onMode={setAuthMode}
              onClose={() => setAuthMode(null)}
              username={session.user.username}
              nickname={session.user.nickname}
              onBind={async (username, password, passwordConfirm, nickname) => {
                await api.bind(username, password, passwordConfirm, nickname);
                await loadPlayer();
                setAuthMode(null);
              }}
              onLogin={async (username, password) => {
                await api.login(username, password);
                applyCombatState(clearedCombatState());
                setHubTab("equipment");
                await loadPlayer();
                setAuthMode(null);
              }}
              onNickname={async (nickname) => {
                await api.updateNickname(nickname);
                await loadPlayer();
              }}
              onPassword={async (currentPassword, password, passwordConfirm) => {
                await api.changePassword(currentPassword, password, passwordConfirm);
              }}
              onDelete={async () => {
                if (!window.confirm("确认删除账号？当前进度和会话将立即失效。")) return;
                await api.deleteAccount();
                applyCombatState(clearedCombatState());
                setMe(null);
                setInventory(null);
                setGrowth(null);
                setLifeProfile(null);
                setTasks(null);
                setSocial(null);
                setHubTab("equipment");
                setAuthMode(null);
              }}
              onLogout={logout}
            />
          )}
        </>
      )}
    </div>
  );
}
